/**
 * Safe reads for Vite env vars — never throws; never calls string methods on undefined.
 */

const isBrowser = typeof window !== 'undefined';

/** CCC SPA host (dev :3001 or production ccc.*). */
export function isCccBrowserHost(): boolean {
  if (!isBrowser) return false;
  const { port, hostname } = window.location;
  return port === '3001' || hostname.toLowerCase().includes('ccc.');
}

export function readViteEnv(key: string): string {
  try {
    const env = import.meta.env as Record<string, unknown> | undefined;
    const raw = env?.[key];
    if (typeof raw === 'string') return raw.trim();
    if (raw != null) return String(raw).trim();
  } catch {
    /* ignore */
  }
  return '';
}

/** Strip trailing slash; never throws. */
export function stripTrailingSlash(value: string | null | undefined): string {
  if (!value || typeof value !== 'string') return '';
  return value.replace(/\/+$/, '');
}

/**
 * Join API origin + path safely.
 * When base is '' (CCC prod behind Nginx), returns same-origin path e.g. `/api/...`.
 */
export function joinApiUrl(base: string | null | undefined, apiPath: string): string {
  const path = apiPath.startsWith('/') ? apiPath : `/${apiPath}`;
  const normalizedBase = stripTrailingSlash(base);
  return normalizedBase ? `${normalizedBase}${path}` : path;
}

/**
 * Safe wrapper for `new URL(path, base)` — empty base uses current origin in browser.
 */
export function safeApiUrl(path: string, base?: string | null): URL {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const normalizedBase = stripTrailingSlash(base);
  if (normalizedBase) {
    return new URL(normalizedPath, normalizedBase.endsWith('/') ? normalizedBase : `${normalizedBase}/`);
  }
  if (isBrowser) {
    return new URL(normalizedPath, window.location.origin);
  }
  return new URL(normalizedPath, 'http://127.0.0.1');
}

/** Main CRM API base — env override first, then same-origin on CCC host. */
export function resolveMainApiBase(): string {
  const configured = stripTrailingSlash(readViteEnv('VITE_MAIN_API_URL'));
  if (configured) return configured;
  if (isCccBrowserHost()) return '';
  if (import.meta.env?.DEV) return 'http://127.0.0.1:5000';
  return '';
}

/** CCC API base — env override first, then same-origin on CCC host. */
export function resolveCccApiBase(): string {
  const configured = stripTrailingSlash(readViteEnv('VITE_CCC_API_URL'));
  if (configured) return configured;
  if (isCccBrowserHost()) return '';
  return '';
}
